import { Alert, Badge, Code, Group, Select, Table, Text, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { listAuditEvents, type AuditEvent } from "@/api/portal";
import { useIAMAuth } from "@/auth/iam";
import { AppLayout } from "@/components/AppLayout";
import { TableSkeleton } from "@/components/TableSkeleton";
import { t } from "@/i18n";

/**
 * Client Portal — the organization's audit log.
 *
 * This is the screen that makes the ADR-0051 operational commitment real. The
 * seven A.1 clinical triggers (suicidal ideation, hypertensive crisis, severe
 * anemia, …) are emitted as `notification.trigger.*` audit events at
 * `severity=critical`, and we promised the platform team that a named human
 * reviews them. Until now they were emitted into a store with no reader.
 *
 * The default filter is therefore the reviewer's query, not a generic log dump.
 *
 * **The empty state is dangerous here.** Every clinical trigger carries
 * `is_phi=true`, so the audit service withholds all of them from a caller
 * without the PHI-audit permission — and returns 200 with an empty list, not an
 * error. A reviewer lacking the privilege would see "no alerts" and conclude no
 * patient needed attention. So we say which case this is, rather than letting a
 * blank table speak for itself.
 *
 * ⚠️ **`phi_visible` comes from the SERVER, not the token.** This page used to
 * compute it as `permissions.includes("phi:audit:read")` — off the JWT's
 * `permissions` claim. Real IAM never populates that claim (only the advisory
 * `permissions_count`), so the check was `false` for *everyone*, including an
 * actual Reviewer who could see every row; and the key it looked for
 * (`phi:audit:read`) is not IAM's real 4-part key either. The BFF resolves the
 * caller's permissions from IAM and tells us the truth — the one place that can.
 *
 * Aghiad's 2026-07-14 model makes this the COMMON case, not an edge one: a
 * ClientAdmin deliberately does not hold the PHI permission (PHI is request-only,
 * via the group-access approval workflow). So the admin who opens this screen is
 * *expected* to see an empty critical-trigger table, and must be told that it
 * means "you are not being shown them" — not "there are none".
 */

const PRESETS = {
  critical: { eventType: "notification.trigger.", severity: "critical" },
  triggers: { eventType: "notification.trigger.", severity: undefined },
  everything: { eventType: undefined, severity: undefined },
} as const;

type PresetKey = keyof typeof PRESETS;

const WINDOW_HOURS: Record<string, number> = { "24h": 24, "7d": 24 * 7, "30d": 24 * 30 };

const SEVERITY_COLOR: Record<string, string> = {
  critical: "red",
  urgent: "orange",
  warn: "yellow",
  info: "gray",
};

export function PortalAuditPage(): JSX.Element {
  const { organizationId } = useIAMAuth();
  const [preset, setPreset] = useState<PresetKey>("critical");
  const [timeWindow, setTimeWindow] = useState("7d");

  // Advance the window instead of freezing it at first fetch. `since` is bucketed to
  // the minute and that bucket is part of the queryKey, so the fetched "last 24h/7d"
  // window moves forward as time passes; a 60s refetchInterval keeps an open
  // critical-alert screen current (otherwise staleTime pins stale data as reassurance
  // on the one screen whose freshness is safety-relevant — ADR-0051).
  const nowMinute = Math.floor(Date.now() / 60_000);
  const since = new Date(
    nowMinute * 60_000 - (WINDOW_HOURS[timeWindow] ?? 168) * 60 * 60 * 1000,
  ).toISOString();

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["portal-audit", preset, timeWindow, nowMinute],
    queryFn: () => listAuditEvents({ ...PRESETS[preset], since, limit: 200 }),
    retry: false,
    refetchInterval: 60_000,
  });

  // Server truth. Assume PHI is HIDDEN until the response says otherwise: while
  // the query is in flight (or if it failed) the honest statement is "we cannot
  // vouch that you are seeing everything", and that is the safe default for a
  // screen whose blank state would otherwise read as reassurance.
  const canSeePhi = data?.phi_visible ?? false;

  return (
    <AppLayout
      breadcrumbs={[{ label: t("portal.title"), to: "/portal" }, { label: t("portal.audit") }]}
    >
      <Group justify="space-between" align="baseline">
        <Title order={1}>{t("portal.audit")}</Title>
        <Text c="dimmed" size="sm">
          {t("portal.organization")}: <Code>{organizationId}</Code>
        </Text>
      </Group>
      <Text c="dimmed" mt="xs">
        {t("portal.audit_subtitle")}
      </Text>

      <Group mt="md" mb="md" align="flex-end">
        <Select
          label={t("portal.audit_preset")}
          data={[
            { value: "critical", label: t("portal.audit_preset_critical") },
            { value: "triggers", label: t("portal.audit_preset_all_triggers") },
            { value: "everything", label: t("portal.audit_preset_everything") },
          ]}
          value={preset}
          allowDeselect={false}
          onChange={(v) => {
            if (v !== null) setPreset(v as PresetKey);
          }}
        />
        <Select
          label={t("portal.audit_window")}
          data={[
            { value: "24h", label: t("portal.audit_window_24h") },
            { value: "7d", label: t("portal.audit_window_7d") },
            { value: "30d", label: t("portal.audit_window_30d") },
          ]}
          value={timeWindow}
          allowDeselect={false}
          onChange={(v) => {
            if (v !== null) setTimeWindow(v);
          }}
        />
      </Group>

      {!canSeePhi && (
        <Alert color="yellow" mb="md" title={t("portal.audit_phi_title")}>
          <Text size="sm">{t("portal.audit_phi_note")}</Text>
        </Alert>
      )}

      {isPending ? (
        <TableSkeleton label={t("portal.audit_loading")} />
      ) : isError ? (
        <Alert color="red" title={t("portal.audit_unavailable")}>
          {error instanceof Error ? error.message : t("authoring.error_generic")}
        </Alert>
      ) : data.events.length === 0 ? (
        <Text c="dimmed">{t("portal.audit_empty")}</Text>
      ) : (
        <>
          <Text size="sm" c="dimmed" mb="xs">
            {data.count} {t("portal.audit_count_suffix")}
          </Text>
          <EventTable events={data.events} />
        </>
      )}
    </AppLayout>
  );
}

function EventTable({ events }: { events: AuditEvent[] }): JSX.Element {
  return (
    <Table striped highlightOnHover withTableBorder>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>{t("portal.audit_col_time")}</Table.Th>
          <Table.Th>{t("portal.audit_col_event")}</Table.Th>
          <Table.Th>{t("portal.audit_col_severity")}</Table.Th>
          <Table.Th>{t("portal.audit_col_patient")}</Table.Th>
          <Table.Th>{t("portal.audit_col_service")}</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {events.map((event) => (
          <Table.Tr key={event.event_id}>
            <Table.Td>{event.occurred_at ?? event.received_at ?? "—"}</Table.Td>
            <Table.Td>
              <Code>{event.event_type}</Code>
            </Table.Td>
            <Table.Td>
              <Badge color={SEVERITY_COLOR[event.severity.toLowerCase()] ?? "blue"} variant="light">
                {event.severity}
              </Badge>
            </Table.Td>
            <Table.Td>{event.patient_id ?? "—"}</Table.Td>
            <Table.Td>{event.service}</Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}
